import { useState } from 'react'
import { format } from 'date-fns'
import Icon from '@components/utils/Icon'

import clsx from 'clsx'

import { Popover, PopoverContent, PopoverTrigger } from '@atoms/Popover'
import { Calendar } from '@atoms/Calendar'
import { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form'
import { scopedTranslation } from '@frontend/utils/I18n'

type DatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>
}

const tDatePicker = scopedTranslation('components.date_picker')

export function DatePicker({ field }: DatePickerProps) {
  const date = field.value ? new Date(field.value) : undefined
  const [isOpen, setIsOpen] = useState(false)

  const handleDayPickerSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      field.onChange(null)
    } else {
      field.onChange(format(selectedDate, 'yyyy-MM-dd'))
      setIsOpen(false)
    }
  }

  return (
    <Popover modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={clsx(
            'tw:border tw:rounded tw:w-full tw:min-h-10.5 tw:border-gray-400 tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100'
          )}
        >
          <span className={clsx('tw:px-3 tw:py-2 tw:flex tw:items-center', date ? 'tw:text-gray-500' : '')}>
            <Icon type="calendar-date" className={clsx('tw:mr-2 tw:h-4 tw:w-4')} />
            <span className="tw:leading-none">
              {date ? (
                <>
                  <span className="tw:sr-only">{tDatePicker('selected_date')}</span>
                  <span>{format(date, 'dd/MM/yyyy')}</span>
                </>
              ) : (
                tDatePicker('placeholder')
              )}
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <Calendar
          mode="single"
          today={new Date()}
          defaultMonth={date}
          selected={date}
          onSelect={handleDayPickerSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
